import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  submitDownloadJobAction,
  resetSubmitDownloadJobAction,
} from 'src/redux/actions/documentBundleActions';

export interface SubmitPayload {
  job_name: string;
  filters: Record<string, unknown>;
  packaging_options: Record<string, unknown>;
}

/**
 * Only fields named here reach the wire — the flattening below is explicit, not a
 * spread. A new field added to the payload and not added here is dropped silently,
 * which is how document_uuids would have been lost.
 */
const omitNull = (entries: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(entries).filter(([, v]) => v !== null && v !== undefined));

export interface SubmitResult {
  job_id: string;
  status: 'QUEUED';
  job_name: string;
}

export function useSubmitDownloadJob() {
  const dispatch = useDispatch();
  const { response, loading, error } = useSelector((state: any) => state.submitDownloadJobReducer);

  const submit = useCallback(
    (payload: SubmitPayload) => {
      dispatch(
        submitDownloadJobAction({
          job_name: payload.job_name,
          date_from: payload.filters.date_from,
          date_to: payload.filters.date_to,
          payer_type: payload.filters.payer_type ?? null,
          payer_name: payload.filters.payer_name ?? null,
          discipline: payload.filters.discipline ?? null,
          doc_type: payload.filters.doc_type ?? null,
          patient_ids: payload.filters.patient_ids ?? null,
          date_scope: payload.packaging_options.date_scope ?? null,
          group_by: payload.packaging_options.group_by ?? null,
          document_handling: payload.packaging_options.document_handling ?? null,
          order_by: payload.packaging_options.order_by ?? null,
          // SQ-163: omitted entirely rather than sent as null, so cohort mode's
          // payload is byte-for-byte what it was and the resolver still runs filters.
          ...omitNull({
            document_uuids: payload.filters.document_uuids,
            adr_reference: payload.filters.adr_reference,
            deliver_as: payload.packaging_options.deliver_as,
            // SQ-171: dropped here, the cohort screen shows one number and the job
            // delivers another — 114 promised against 2,963 delivered on QA.
            therapist_id: payload.filters.therapist_id,
            therapist_scope: payload.filters.therapist_scope,
            // SQ-181: the roster was drawn with these applied, so the job has to resolve
            // with them applied too. Both go through omitNull for the same reason as the
            // therapist pair — a payload that predates them must stay byte-identical.
            include_held_from_billing: payload.filters.include_held_from_billing,
            include_audit_log: payload.packaging_options.include_audit_log,
          }),
        })
      );
    },
    [dispatch]
  );

  const result: SubmitResult | null = response
    ? {
        job_id: response.jobId ?? response.job_id,
        status: 'QUEUED',
        job_name: response.jobName ?? response.job_name,
      }
    : null;

  const reset = useCallback(() => {
    dispatch(resetSubmitDownloadJobAction());
  }, [dispatch]);

  return { submit, loading, result, error, reset };
}
